import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  timeAgo,
  resetStore,
  useT,
  selectLocale,
  useAppSelector,
  AUTH_PROVIDERS,
  MIN_PASSWORD_LENGTH,
  hasPasswordIdentity,
  userDisplayName,
  type AuthProviderId,
  type RootState,
} from "@reciperoll/core";
import type { UserIdentity } from "@supabase/supabase-js";
import {
  LogOut,
  Key,
  RefreshCw,
  RefreshCcw,
  AlertCircle,
  Lock,
  FileText,
  Info,
  HelpCircle,
  Download,
  Trash2,
  ExternalLink,
  ChevronRight,
  Sliders,
} from "react-feather";
import { useAuth, signOut } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { ProviderIcon } from "../components/SocialAuthButtons";
import { store, persistor } from "../store/store";
import { runSync } from "../sync/syncManager";
import { buildExportData } from "../utils/exportData";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { POLICY_URLS as LINKS } from "../lib/urls";
import { useToast } from "../components/Toast";
import * as Sentry from "@sentry/react";

// App version shown in the footer (mirrors the RN account screen). Vite has no
// built-in package-version source, so read a VITE_APP_VERSION env if wired up
// and otherwise fall back to the current app version. See report: a Vite
// `define` sourcing this from package.json should replace the literal fallback.
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "1.0.0";

function initialsOf(name: string) {
  return (
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "?"
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-sm font-bold text-ink-soft uppercase tracking-wider mb-2 mt-7">
      {children}
    </p>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-surface-2 rounded-2xl overflow-hidden">{children}</div>;
}

function Row({
  icon,
  label,
  onClick,
  href,
  trailing = "chevron",
  first,
  destructive,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  href?: string;
  trailing?: "chevron" | "external" | "none";
  first?: boolean;
  destructive?: boolean;
  disabled?: boolean;
}) {
  const cls = `w-full flex items-center gap-3 px-4 py-4 text-left text-base ${
    first ? "" : "border-t border-line"
  } ${disabled ? "opacity-60" : "hover:bg-surface-2/50 dark:hover:bg-surface-2/50 cursor-pointer"} transition-colors`;
  const body = (
    <>
      <span className={`shrink-0 ${destructive ? "text-red-600" : "text-terracotta"}`}>{icon}</span>
      <span className={`flex-1 ${destructive ? "text-red-600" : "text-ink"}`}>{label}</span>
      {trailing === "chevron" && <ChevronRight className="w-[18px] h-[18px] text-ink-faint shrink-0" />}
      {trailing === "external" && <ExternalLink className="w-4 h-4 text-ink-faint shrink-0" />}
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {body}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={cls}>
      {body}
    </button>
  );
}

export default function Settings() {
  const t = useT();
  const locale = useAppSelector(selectLocale);
  const { session } = useAuth();
  const navigate = useNavigate();
  const sync = useAppSelector((s: RootState) => s.sync);
  const toast = useToast();

  // Dev-only: long-press the version footer to fire a Sentry test event, so the
  // pipeline can be verified end to end (mirrors the RN account screen). The web
  // has no "native crash", so this just sends a handled capture. The browser has no
  // long-press event, so we time a pointer-down; handlers are only wired in DEV.
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTestCrash = () => {
    longPress.current = setTimeout(() => {
      Sentry.captureException(
        new Error("Sentry test — settings version long-press"),
      );
      toast.show("Sent test error to Sentry");
    }, 600);
  };
  const cancelTestCrash = () => {
    if (longPress.current) clearTimeout(longPress.current);
    longPress.current = null;
  };

  const fullName = userDisplayName(session?.user.user_metadata) ?? "";
  const email = session?.user.email ?? "";
  const displayName = fullName || email.split("@")[0] || t("home.there");

  const [name, setName] = useState(fullName);
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<string | null>(null);

  // Linked sign-in identities (email + any social providers). Seeded from the
  // session, then refreshed from the server (authoritative after link/unlink).
  const [identities, setIdentities] = useState<UserIdentity[]>(
    session?.user.identities ?? [],
  );
  const hasPw = hasPasswordIdentity(identities);

  const refreshIdentities = async () => {
    const { data } = await supabase.auth.getUserIdentities();
    if (data?.identities) setIdentities(data.identities);
  };
  useEffect(() => {
    refreshIdentities();
  }, []);

  const [linkMsg, setLinkMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [linkBusy, setLinkBusy] = useState<AuthProviderId | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<AuthProviderId | null>(null);

  const connect = async (provider: AuthProviderId) => {
    setLinkBusy(provider);
    setLinkMsg(null);
    // Redirect back to settings; detectSessionInUrl finishes the link on return.
    const { error } = await supabase.auth.linkIdentity({
      provider,
      options: { redirectTo: `${window.location.origin}/settings` },
    });
    if (error) {
      setLinkMsg({ ok: false, text: error.message });
      setLinkBusy(null);
    }
    // On success the browser navigates to the provider.
  };

  const disconnect = async (provider: AuthProviderId) => {
    setDisconnectTarget(null);
    const identity = identities.find((i) => i.provider === provider);
    if (!identity) return;
    setLinkBusy(provider);
    setLinkMsg(null);
    const { error } = await supabase.auth.unlinkIdentity(identity);
    setLinkBusy(null);
    if (error) {
      setLinkMsg({ ok: false, text: error.message });
      return;
    }
    await refreshIdentities();
    setLinkMsg({ ok: true, text: t("account.accountDisconnected") });
  };

  // Change-password form (collapsible). For a social-only account this is a
  // "Set a password" flow (no current password to re-authenticate against).
  const [showPw, setShowPw] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [exporting, setExporting] = useState(false);
  const [confirm, setConfirm] = useState<null | "delete" | "deleteFinal" | "reset">(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // When the session clears (sign out / delete), AuthGate redirects to login.
  if (!session) return null;

  const pending = Object.values(sync.byTable).reduce((n, tbl) => n + tbl.dirtyIds.length, 0);
  const syncSubtitle =
    sync.status === "syncing"
      ? t("account.syncing")
      : sync.status === "error"
        ? sync.lastError ?? t("account.syncFailed")
        : pending > 0
          ? t("account.changesPending", { count: pending })
          : sync.lastSyncAt
            ? t("account.syncedAgo", { time: timeAgo(sync.lastSyncAt, locale) })
            : t("account.notSyncedYet");

  const saveName = async () => {
    const trimmed = name.trim();
    if (trimmed === fullName.trim()) return;
    setSavingName(true);
    setNameMsg(null);
    // Write to `display_name`, not `full_name`: OAuth providers overwrite
    // `full_name` from their token on every social sign-in, so an edited name
    // stored there reverts to the Google/Apple name. `display_name` is untouched
    // by providers (userDisplayName prefers it when reading).
    const { error } = await supabase.auth.updateUser({ data: { display_name: trimmed } });
    setSavingName(false);
    setNameMsg(error ? error.message : t("account.saved"));
    if (!error) setTimeout(() => setNameMsg(null), 2500);
  };

  const closePw = () => {
    setShowPw(false);
    setCurPw("");
    setNewPw("");
    setConfirmPw("");
    setPwMsg(null);
  };

  const changePassword = async () => {
    setPwMsg(null);
    if (newPw.length < MIN_PASSWORD_LENGTH) {
      setPwMsg({ ok: false, text: t("account.passwordMinLength", { count: MIN_PASSWORD_LENGTH }) });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: t("account.newPasswordsDontMatch") });
      return;
    }
    setPwBusy(true);
    // Changing an existing password: re-authenticate with the current password
    // first so a left-open session can't silently change it. A social-only account
    // has no password to verify against — it's just setting one.
    if (hasPw) {
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email,
        password: curPw,
      });
      if (reauthError) {
        setPwBusy(false);
        setPwMsg({ ok: false, text: t("account.currentPasswordIncorrect") });
        return;
      }
    }
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setPwBusy(false);
    if (error) {
      setPwMsg({ ok: false, text: error.message });
      return;
    }
    setCurPw("");
    setNewPw("");
    setConfirmPw("");
    setPwMsg({ ok: true, text: hasPw ? t("account.passwordUpdated") : t("account.passwordSet") });
    // Setting a password adds an `email` identity — reflect it in the UI.
    if (!hasPw) refreshIdentities();
  };

  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    try {
      const json = JSON.stringify(buildExportData(email), null, 2);
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reciperoll-export-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Wipe local state; synced data re-downloads on the next sync.
  const resetLocal = async () => {
    store.dispatch(resetStore());
    await persistor.purge();
  };

  const deleteAccount = async () => {
    setDeleting(true);
    // Server-side: the Edge Function deletes the auth user; DB rows cascade away.
    const { error } = await supabase.functions.invoke("delete-account");
    if (error) {
      setDeleting(false);
      setConfirm(null);
      alert(`${t("account.deleteFailed")}: ${error.message}`);
      return;
    }
    await resetLocal();
    await signOut(); // session clears → AuthGate redirects to login
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="font-display text-3xl font-extrabold text-ink mb-6">{t("account.title")}</h1>

      {/* Profile header */}
      <div className="flex flex-col items-center mb-2">
        <div className="w-20 h-20 rounded-full bg-terracotta flex items-center justify-center mb-3">
          <span className="font-display text-2xl font-extrabold text-white">
            {initialsOf(displayName)}
          </span>
        </div>
        <p className="text-xl font-bold text-ink">{displayName}</p>
        {!!email && <p className="text-sm text-ink-soft mt-0.5">{email}</p>}
      </div>

      <SectionLabel>{t("account.profile")}</SectionLabel>
      <Card>
        <div className="p-4">
          <label className="block text-sm font-semibold text-ink-soft mb-1">{t("account.fullName")}</label>
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("account.yourName")}
              className="flex-1 rounded-xl border border-line bg-surface px-3 py-2.5 text-ink outline-none focus:border-terracotta"
            />
            <button
              type="button"
              onClick={saveName}
              disabled={savingName || name.trim() === fullName.trim()}
              className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 text-sm font-bold text-white disabled:opacity-40 disabled:hover:bg-terracotta cursor-pointer disabled:cursor-default"
            >
              {savingName ? t("account.saving") : t("common.save")}
            </button>
          </div>
          {nameMsg && (
            <p className={`mt-2 text-sm ${nameMsg === t("account.saved") ? "text-green-600" : "text-red-600"}`}>
              {nameMsg}
            </p>
          )}
          <p className="mt-3 text-xs text-ink-faint">
            {t("account.emailChangeHint")}
          </p>
        </div>
        <Row icon={<LogOut className="w-[18px] h-[18px]" />} label={t("account.signOut")} onClick={() => signOut()} />
      </Card>

      <SectionLabel>{t("prefs.title")}</SectionLabel>
      <Card>
        <Row
          first
          icon={<Sliders className="w-[18px] h-[18px]" />}
          label={t("account.preferencesRowSub")}
          onClick={() => navigate("/preferences")}
        />
      </Card>

      <SectionLabel>{t("account.signInMethods")}</SectionLabel>
      <Card>
        {AUTH_PROVIDERS.map((p, i) => {
          const connected = identities.some((id) => id.provider === p.id);
          // Blocked when it's the only sign-in method left (would lock the user out).
          const isLastMethod = identities.length <= 1;
          return (
            <div
              key={p.id}
              className={`flex items-center gap-3 px-4 py-3.5 ${i > 0 ? "border-t border-line" : ""}`}
            >
              <ProviderIcon id={p.id} />
              <span className="flex-1 text-ink">{p.connectLabel}</span>
              {connected ? (
                <button
                  type="button"
                  onClick={() => setDisconnectTarget(p.id)}
                  disabled={linkBusy !== null || isLastMethod}
                  title={isLastMethod ? t("account.addAnotherFirst") : undefined}
                  className="rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-ink-soft hover:bg-surface-2 disabled:opacity-40 cursor-pointer disabled:cursor-default"
                >
                  {linkBusy === p.id ? "…" : t("account.disconnect")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => connect(p.id)}
                  disabled={linkBusy !== null}
                  className="rounded-lg bg-terracotta hover:bg-terracotta-dark transition-colors px-3 py-1.5 text-sm font-bold text-white disabled:opacity-60 cursor-pointer disabled:cursor-default"
                >
                  {linkBusy === p.id ? "…" : t("account.connect")}
                </button>
              )}
            </div>
          );
        })}
      </Card>
      {linkMsg && (
        <p className={`mt-2 text-sm ${linkMsg.ok ? "text-green-600" : "text-red-600"}`}>
          {linkMsg.text}
        </p>
      )}

      <SectionLabel>{t("account.password")}</SectionLabel>
      <Card>
        <Row
          first
          icon={<Key className="w-[18px] h-[18px]" />}
          label={hasPw ? t("account.changePassword") : t("account.setPassword")}
          onClick={() => (showPw ? closePw() : setShowPw(true))}
        />
        {showPw && (
          <div className="p-4 border-t border-line space-y-3">
            {!hasPw && (
              <p className="text-sm text-ink-soft">
                {t("account.addPasswordHint")}
              </p>
            )}
            {hasPw && (
              <input
                type="password"
                autoComplete="current-password"
                placeholder={t("account.currentPassword")}
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-ink outline-none focus:border-terracotta"
              />
            )}
            <input
              type="password"
              autoComplete="new-password"
              placeholder={t("account.newPassword")}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-ink outline-none focus:border-terracotta"
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder={t("account.confirmNewPassword")}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && changePassword()}
              className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-ink outline-none focus:border-terracotta"
            />
            {pwMsg && (
              <p className={`text-sm ${pwMsg.ok ? "text-green-600" : "text-red-600"}`}>{pwMsg.text}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closePw}
                className="rounded-xl border border-line px-4 py-2 text-sm font-semibold text-ink-soft hover:bg-surface-2 cursor-pointer"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={changePassword}
                disabled={pwBusy || (hasPw && !curPw) || !newPw || !confirmPw}
                className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2 text-sm font-bold text-white disabled:opacity-40 disabled:hover:bg-terracotta cursor-pointer disabled:cursor-default"
              >
                {pwBusy ? t("account.saving") : hasPw ? t("account.updatePassword") : t("account.setPasswordShort")}
              </button>
            </div>
          </div>
        )}
      </Card>

      <SectionLabel>{t("account.sync")}</SectionLabel>
      <Card>
        <button
          type="button"
          onClick={() => runSync()}
          className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-surface-2/50 dark:hover:bg-surface-2/50 transition-colors cursor-pointer"
        >
          <span className={`shrink-0 ${sync.status === "error" ? "text-red-600" : "text-terracotta"}`}>
            {sync.status === "error" ? (
              <AlertCircle className="w-[18px] h-[18px]" />
            ) : (
              <RefreshCw className={`w-[18px] h-[18px] ${sync.status === "syncing" ? "animate-spin" : ""}`} />
            )}
          </span>
          <span className="flex-1">
            <span className="flex items-center">
              <span className="block text-base text-ink">{t("account.sync")}</span>
              {!sync.isOnline ? (
                <span className="flex items-center ml-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink-faint mr-1" />
                  <span className="text-[10px] font-medium text-ink-soft">
                    {t("account.offline")}
                  </span>
                </span>
              ) : sync.realtimeConnected ? (
                <span className="flex items-center ml-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1" />
                  <span className="text-[10px] font-medium text-green-600 dark:text-green-500">
                    {t("account.live")}
                  </span>
                </span>
              ) : null}
            </span>
            <span className={`block text-xs ${sync.status === "error" ? "text-red-600" : "text-ink-soft"}`}>
              {syncSubtitle}
            </span>
          </span>
        </button>
      </Card>

      <SectionLabel>{t("account.about")}</SectionLabel>
      <Card>
        <Row first icon={<Lock className="w-[18px] h-[18px]" />} label={t("account.privacyPolicy")} trailing="external" href={LINKS.privacy} />
        <Row icon={<FileText className="w-[18px] h-[18px]" />} label={t("account.termsOfService")} trailing="external" href={LINKS.terms} />
        <Row icon={<Sliders className="w-[18px] h-[18px]" />} label={t("account.cookiePreferences")} onClick={() => window.dispatchEvent(new Event("rr:open-consent"))} />
        <Row icon={<Info className="w-[18px] h-[18px]" />} label={t("account.about")} trailing="external" href={LINKS.about} />
        <Row icon={<HelpCircle className="w-[18px] h-[18px]" />} label={t("account.helpSupport")} trailing="external" href={LINKS.support} />
      </Card>

      <SectionLabel>{t("account.data")}</SectionLabel>
      <Card>
        <Row
          first
          icon={<Download className="w-[18px] h-[18px]" />}
          label={exporting ? t("account.preparingExport") : t("account.exportData")}
          trailing="none"
          disabled={exporting}
          onClick={handleExport}
        />
      </Card>

      <SectionLabel>{t("account.dangerZone")}</SectionLabel>
      <Card>
        <Row
          first
          destructive
          icon={<Trash2 className="w-[18px] h-[18px]" />}
          label={deleting ? t("account.deleting") : t("account.deleteAccount")}
          disabled={deleting}
          onClick={() => setConfirm("delete")}
        />
      </Card>

      {import.meta.env.DEV && (
        <>
          <SectionLabel>{t("account.developer")}</SectionLabel>
          <Card>
            <Row
              first
              icon={<RefreshCcw className="w-[18px] h-[18px]" />}
              label={t("account.resetLocalData")}
              trailing="none"
              onClick={() => setConfirm("reset")}
            />
          </Card>
        </>
      )}

      <div className="flex flex-col items-center gap-1 mt-10 mb-4">
        <p
          className="text-xs text-ink-faint"
          onPointerDown={import.meta.env.DEV ? startTestCrash : undefined}
          onPointerUp={import.meta.env.DEV ? cancelTestCrash : undefined}
          onPointerLeave={import.meta.env.DEV ? cancelTestCrash : undefined}
        >
          {t("account.version", { version: APP_VERSION })}
        </p>
      </div>

      {confirm === "delete" && (
        <ConfirmDialog
          title={t("account.deleteConfirmTitle")}
          message={t("account.deleteMessage")}
          confirmLabel={t("account.deleteAccount")}
          confirmClass="bg-red-500 hover:bg-red-600"
          onConfirm={() => setConfirm("deleteFinal")}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === "deleteFinal" && (
        <ConfirmDialog
          title={t("account.deleteFinalTitle")}
          message={t("account.deleteFinalMessage")}
          confirmLabel={deleting ? t("account.deleting") : t("account.deleteYes")}
          confirmClass="bg-red-500 hover:bg-red-600"
          reverseButtons
          onConfirm={deleteAccount}
          onCancel={() => setConfirm(null)}
        />
      )}
      {disconnectTarget && (
        <ConfirmDialog
          title={t("account.disconnectTitle", { provider: AUTH_PROVIDERS.find((p) => p.id === disconnectTarget)?.connectLabel })}
          message={t("account.disconnectMessage")}
          confirmLabel={t("account.disconnect")}
          confirmClass="bg-red-500 hover:bg-red-600"
          onConfirm={() => disconnect(disconnectTarget)}
          onCancel={() => setDisconnectTarget(null)}
        />
      )}
      {confirm === "reset" && (
        <ConfirmDialog
          title={t("account.resetConfirmTitle")}
          message={t("account.resetMessage")}
          confirmLabel={t("account.reset")}
          confirmClass="bg-red-500 hover:bg-red-600"
          onConfirm={async () => {
            await resetLocal();
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
